"use client";

import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";

const useIsomorphicLayoutEffect =
	typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Tracks each open modal in a Set keyed by a stable per-instance object so
// that re-running the effect (StrictMode, HMR, Radix Presence remounts) is
// idempotent — the same key can be added repeatedly without changing the size.
const openModalKeys = new Set<object>();
const listeners = new Set<() => void>();

const notify = () => {
	for (const listener of listeners) {
		listener();
	}
};

export const getOpenModalCount = () => openModalKeys.size;

export const subscribeModalRegistry = (callback: () => void) => {
	listeners.add(callback);
	return () => {
		listeners.delete(callback);
	};
};

const getServerSnapshot = () => 0;

export const useIsAnyModalOpen = () =>
	useSyncExternalStore(
		subscribeModalRegistry,
		getOpenModalCount,
		getServerSnapshot,
	) > 0;

// Returns the modal's 1-indexed mount-order depth in the open-modal stack.
// Returns 0 before the layout effect runs; consumers should treat 0 as "not
// yet stacked" and fall back to the base z-index.
//
// IMPORTANT: call this from a component that only mounts while the modal is
// actually open (inside Radix's Presence-gated content), not from the
// always-mounted content wrapper — pages keep closed dialogs mounted, and a
// closed dialog must not count as open. Use ModalStackDepthReporter for the
// Dialog/Sheet wrappers.
export const useModalStackDepth = () => {
	const keyRef = useRef<object | null>(null);
	if (keyRef.current === null) {
		keyRef.current = {};
	}
	const [depth, setDepth] = useState(0);

	useIsomorphicLayoutEffect(() => {
		const key = keyRef.current;
		if (!key) {
			return;
		}
		openModalKeys.add(key);
		setDepth(openModalKeys.size);
		notify();
		return () => {
			openModalKeys.delete(key);
			notify();
		};
	}, []);

	return depth;
};

// Registers an open modal for the lifetime of its mount and reports its
// 1-indexed stack depth upward. Rendered inside the Presence-gated content of
// Dialog/Sheet, so it mounts when the modal opens and unmounts when it closes
// (after any exit animation) — a closed-but-mounted modal never registers.
export const ModalStackDepthReporter = ({
	onDepthChange,
}: {
	onDepthChange: (depth: number) => void;
}) => {
	const depth = useModalStackDepth();
	useIsomorphicLayoutEffect(() => {
		onDepthChange(depth);
		return () => {
			onDepthChange(0);
		};
	}, [depth, onDepthChange]);
	return null;
};

// Returns a z-index value guaranteed to sit above the topmost open modal,
// using the same 20-unit step as `useModalStackDepth`. Used by portal-rendered
// child primitives (Popover, Select, DropdownMenu, Tooltip, etc.) so they
// remain visible when opened inside a stacked Dialog/Sheet — without it, the
// modal's bumped content z-index would cover the child portal.
//
// Captures the modal count once at mount; the value is stable for the
// primitive's lifetime, which is fine because these popovers are short-lived
// and the surrounding modal stack rarely changes while one is open.
export const useLayerOverModals = (base = 50) => {
	const [zIndex, setZIndex] = useState(base);

	useIsomorphicLayoutEffect(() => {
		setZIndex(base + openModalKeys.size * 20);
	}, [base]);

	return zIndex;
};

export const __resetModalRegistryForTests = () => {
	openModalKeys.clear();
	listeners.clear();
};
